import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAppSelector } from "../../hooks/useAppSelector";
import { itemService } from "../../api/services/itemService";
import {
  selectCategoryByName,
  selectBrandsByCategory,
} from "../../features/items/itemSelectors";
import {
  getCartCount,
  getCartItems,
  removeFromCart,
} from "../../utils/cartUtils";
import { isUserLoggedIn } from "../../utils/authUtils";
import { useCanViewPrices } from "../../utils/pricingVisibility";
import Loader from "../Loader/Loader";
import logoImage from "../../images/wireox-logo.png";
import defaultProductImage from "../../images/Default-Image.jpeg";
import cutoffVehicleImage from "../../images/CutOffVeh.png";

const DEFAULT_SHIPPING_TIME_ZONE = "America/New_York";

const NAV_ITEMS_WITH_API_SCOPE = ["apple", "samsung", "motorola", "google"];

const NavbarComponent = ({
  activeNavItem,
  isClosing,
  handleNavItemClick,
  handleDropdownOptionClick,
  searchQuery,
  setSearchQuery,
  handleSearch,
  showSearchModal,
  searchResults = { models: [], products: [] },
  searchLoading,
  closeSearchModal,
  showServicesDropdown,
  setShowServicesDropdown,
  timeLeft,
  showShippingDropdown,
  setShowShippingDropdown,
  selectedShipping,
  setSelectedShipping,
  shippingMethods,
  selectedMethod,
  activeTab,
  setActiveTab,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const showPrices = useCanViewPrices();
  const [selectedOptions, setSelectedOptions] = useState({});
  const [clockTick, setClockTick] = useState(Date.now());
  const [showCartDropdown, setShowCartDropdown] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [hoveredBrandName, setHoveredBrandName] = useState(null);
  const [selectedBrandName, setSelectedBrandName] = useState(null);
  const [cartCount, setCartCount] = useState(0);
  const [cartTotal, setCartTotal] = useState(0);
  const [isNavigating, setIsNavigating] = useState(false);
  const [navDropdownQuery, setNavDropdownQuery] = useState("");
  const [navDropdownApiModels, setNavDropdownApiModels] = useState([]);
  const [navDropdownSearchLoading, setNavDropdownSearchLoading] =
    useState(false);
  const navDropdownSearchDebounceRef = useRef(null);
  const navDropdownSearchSeqRef = useRef(0);

  // Map nav item names to category names
  const getCategoryName = (navItem) => {
    const mapping = {
      apple: "Apple",
      samsung: "Samsung",
      motorola: "Motorola",
      google: "Google",
    };
    return mapping[navItem] || navItem;
  };

  // Get category data from Redux
  const categoryName = activeNavItem ? getCategoryName(activeNavItem) : null;
  useAppSelector((state) => selectCategoryByName(state, categoryName));
  const brands = useAppSelector((state) =>
    selectBrandsByCategory(state, categoryName),
  );

  const scopeNavForApi =
    activeNavItem && NAV_ITEMS_WITH_API_SCOPE.includes(activeNavItem);

  const brandsForSearch = useMemo(() => {
    if (!brands?.length) return [];
    const q = navDropdownQuery.trim().toLowerCase();
    if (!q) return brands;

    return brands
      .map((b) => ({
        ...b,
        Model: (b.Model || []).filter((m) => {
          const name = (m.ModelName || "").toLowerCase();
          return (
            name.includes(q) || (b.BrandName || "").toLowerCase().includes(q)
          );
        }),
      }))
      .filter(
        (b) =>
          (b.BrandName || "").toLowerCase().includes(q) ||
          (b.Model && b.Model.length > 0),
      );
  }, [brands, navDropdownQuery]);

  const localModelNameKeys = useMemo(() => {
    const s = new Set();
    brandsForSearch.forEach((b) =>
      (b.Model || []).forEach((m) => {
        if (m.ModelName) s.add(String(m.ModelName).trim().toLowerCase());
      }),
    );
    return s;
  }, [brandsForSearch]);

  const navDropdownApiExtras = useMemo(() => {
    return (navDropdownApiModels || []).filter(
      (m) =>
        m.Model &&
        !localModelNameKeys.has(String(m.Model).trim().toLowerCase()),
    );
  }, [navDropdownApiModels, localModelNameKeys]);

  useEffect(() => {
    navDropdownSearchSeqRef.current += 1;
    setNavDropdownQuery("");
    setNavDropdownApiModels([]);
    setNavDropdownSearchLoading(false);
  }, [activeNavItem]);

  useEffect(() => {
    const q = navDropdownQuery.trim();

    if (navDropdownSearchDebounceRef.current) {
      clearTimeout(navDropdownSearchDebounceRef.current);
      navDropdownSearchDebounceRef.current = null;
    }

    if (!scopeNavForApi || q.length < 2) {
      navDropdownSearchSeqRef.current += 1;
      setNavDropdownApiModels([]);
      setNavDropdownSearchLoading(false);
      return undefined;
    }

    setNavDropdownSearchLoading(true);
    const requestSeq = ++navDropdownSearchSeqRef.current;
    navDropdownSearchDebounceRef.current = setTimeout(async () => {
      navDropdownSearchDebounceRef.current = null;
      try {
        const res = await itemService.getItemAndModelWithSearch(q);
        if (requestSeq !== navDropdownSearchSeqRef.current) return;
        const want = getCategoryName(activeNavItem).toLowerCase();
        const list = (res.ModelList || []).filter((m) => {
          const make = String(m.Make || "")
            .trim()
            .toLowerCase();
          return make === want;
        });
        setNavDropdownApiModels(list.slice(0, 14));
      } catch {
        if (requestSeq === navDropdownSearchSeqRef.current) {
          setNavDropdownApiModels([]);
        }
      } finally {
        if (requestSeq === navDropdownSearchSeqRef.current) {
          setNavDropdownSearchLoading(false);
        }
      }
    }, 350);

    return () => {
      if (navDropdownSearchDebounceRef.current) {
        clearTimeout(navDropdownSearchDebounceRef.current);
        navDropdownSearchDebounceRef.current = null;
      }
    };
  }, [navDropdownQuery, activeNavItem, scopeNavForApi]);

  // Re-render once per second so cutoff countdown stays current.
  useEffect(() => {
    const timer = setInterval(() => {
      setClockTick(Date.now());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Update cart count and total
  const updateCartInfo = () => {
    const count = getCartCount();
    const items = getCartItems();
    const total = items.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0,
    );
    setCartCount(count);
    setCartTotal(total);
  };

  // Load cart info on mount and listen for updates
  useEffect(() => {
    updateCartInfo();

    // Listen for cart updates
    const handleCartUpdate = () => {
      updateCartInfo();
    };

    window.addEventListener("cartUpdated", handleCartUpdate);

    return () => {
      window.removeEventListener("cartUpdated", handleCartUpdate);
    };
  }, []);

  const parseCutoffTo24Hour = (cutoffTime) => {
    if (!cutoffTime || typeof cutoffTime !== "string")
      return { hours: 23, minutes: 59 };
    const match = cutoffTime
      .trim()
      .match(/^(\d{1,2})\s*:\s*(\d{2})\s*(AM|PM)$/i);
    if (!match) return { hours: 23, minutes: 59 };
    let h = Number(match[1]);
    const m = Number(match[2]);
    const period = match[3].toUpperCase();
    if (period === "AM") h = h === 12 ? 0 : h;
    if (period === "PM") h = h === 12 ? 12 : h + 12;
    return { hours: h, minutes: m };
  };

  const getNowInTimeZoneParts = (timeZone) => {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    const parts = formatter.formatToParts(new Date(clockTick));
    const get = (type) =>
      Number(parts.find((p) => p.type === type)?.value || "0");
    return {
      hours: get("hour"),
      minutes: get("minute"),
      seconds: get("second"),
    };
  };

  // Calculate countdown until today's cutoff (or next day's if passed).
  const getMethodTime = (method) => {
    const tz = method?.timeZone || DEFAULT_SHIPPING_TIME_ZONE;
    const now = getNowInTimeZoneParts(tz);
    const cutoff = parseCutoffTo24Hour(method?.cutoffTime);
    const nowTotal = now.hours * 3600 + now.minutes * 60 + now.seconds;
    const cutoffTotal = cutoff.hours * 3600 + cutoff.minutes * 60;
    let totalSeconds = cutoffTotal - nowTotal;
    if (totalSeconds < 0) totalSeconds += 24 * 3600;
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return { hours, minutes, seconds };
  };

  const handleCategoryClick = (category) => {
    handleDropdownOptionClick(category);
    navigate(`/category/${category}`);
  };

  const handleOptionSelect = (modelName, e) => {
    e.preventDefault();
    setSelectedOptions((prev) => ({
      ...prev,
      [activeNavItem]: modelName,
    }));
    // Navigate to category page with model parameter
    // Close dropdown first by calling handleNavItemClick with null
    handleNavItemClick(null, e);
    // Navigate to category page with model as query parameter
    setTimeout(() => {
      navigate(
        `/category/${activeNavItem}?model=${encodeURIComponent(modelName)}`,
      );
    }, 100);
  };

  // Clear selected brand when nav item changes or dropdown closes (do not lock body scroll —
  // overflow:hidden removes the scrollbar and shifts the layout).
  useEffect(() => {
    setSelectedBrandName(null);
  }, [activeNavItem]);

  // Close category dropdown when clicking outside the nav bar and dropdown panel
  useEffect(() => {
    if (!activeNavItem) return undefined;

    const handleMouseDown = (event) => {
      // Keep open only when interacting inside the dropdown content itself.
      if (event.target.closest(".dropdown-container")) {
        return;
      }
      handleNavItemClick(null, {
        preventDefault: () => {},
      });
    };

    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [activeNavItem, handleNavItemClick]);

  // Close cart dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showCartDropdown &&
        !event.target.closest(".cart-container") &&
        !event.target.closest(".mobile-cart-icon") &&
        !event.target.closest(".cart-dropdown-item-delete") &&
        !event.target.closest(".cart-dropdown") &&
        !event.target.closest(".mobile-cart-dropdown")
      ) {
        setShowCartDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showCartDropdown]);

  // Close services dropdown when clicking outside (desktop menu + mobile services icon)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showServicesDropdown &&
        !event.target.closest(".services-menu") &&
        !event.target.closest(".mobile-service-icon")
      ) {
        setShowServicesDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showServicesDropdown, setShowServicesDropdown]);

  // Close shipping / cutoff times dropdown when clicking outside (keep open for clicks inside timer + panel)
  useEffect(() => {
    if (!showShippingDropdown) return undefined;

    const handleMouseDown = (event) => {
      if (event.target.closest(".shipping-timer")) {
        return;
      }
      setShowShippingDropdown(false);
    };

    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [showShippingDropdown, setShowShippingDropdown]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileMenuOpen && !event.target.closest(".mobile-menu-container")) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  // Reset navigation loader when location changes
  useEffect(() => {
    setIsNavigating(false);
  }, [location.pathname]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const selectedMethodTime = selectedMethod
    ? getMethodTime(selectedMethod)
    : timeLeft;

  return (
    <>
      {/* Loader Overlay */}
      {isNavigating && <Loader />}

      {/* Mobile Shipping Timer */}
      <div className="msfedex-mobile">
        <div className="msfedex-timer msfedex-timerblock disp_block">
          <div className="s-method-name">
            <p id="cutofftime_name">
              <strong className="method-change">
                {selectedMethod?.name || "Amazon"}
              </strong>
              <span className="method-type">
                {selectedMethod?.type || "ground"}
              </span>
              <span className="mobile-cutoff-time">
                | Cutoff: {selectedMethod?.cutoffTime || "N/A"}
              </span>
            </p>
          </div>
          <div className="s-timer-block">
            <div className="fd-timer">
              <strong>
                {String(selectedMethodTime.hours).padStart(2, "0")}
              </strong>
              <span>:</span>
              <strong>
                {String(selectedMethodTime.minutes).padStart(2, "0")}
              </strong>
              <span>:</span>
              <strong>
                {String(selectedMethodTime.seconds).padStart(2, "0")}
              </strong>
            </div>
          </div>
        </div>
      </div>

      {/* Header */}
      <header className="header">
        <div className="header-container">
          {/* Left Side - Hamburger + Logo */}
          <div className="header-left">
            {/* Mobile Hamburger Menu */}
            <button
              className="mobile-menu-toggle"
              onClick={toggleMobileMenu}
              aria-label="Toggle mobile menu"
            >
              <span
                className={`hamburger-line ${isMobileMenuOpen ? "active" : ""}`}
              ></span>
              <span
                className={`hamburger-line ${isMobileMenuOpen ? "active" : ""}`}
              ></span>
              <span
                className={`hamburger-line ${isMobileMenuOpen ? "active" : ""}`}
              ></span>
            </button>

            {/* Logo */}
            <a href="/" className="logo" title="WIRE OX USA">
              <div className="logo-container">
                <img
                  src={logoImage}
                  className="logo-image"
                  alt="WIRE OX Logo"
                />
              </div>
            </a>

            {/* Three Line Menu Button */}
            <button
              className="three-line-menu-toggle"
              onClick={toggleMobileMenu}
              aria-label="Toggle navigation menu"
            >
              <span className="three-line"></span>
              <span className="three-line"></span>
              <span className="three-line"></span>
            </button>
          </div>

          {/* Right Side - Mobile Icons */}
          <div className="mobile-icons">
            {/* Services Icon */}
            <button
              className="mobile-service-icon"
              onClick={() => setShowServicesDropdown(!showServicesDropdown)}
              aria-label="Services"
            >
              <span className="service-icon-svg"></span>
            </button>

            {/* Account Icon */}
            <button
              onClick={(e) => {
                handleNavItemClick(null, e);
                const dest = isUserLoggedIn() ? "/my-account" : "/login";
                if (location.pathname !== dest) {
                  setIsNavigating(true);
                }
                navigate(dest);
              }}
              className="mobile-account-icon"
              aria-label="My Account"
              style={{ background: "none", border: "none", cursor: "pointer" }}
            >
              <span
                className="account-icon account-icon--mobile"
                aria-hidden="true"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <circle
                    cx="12"
                    cy="8.5"
                    r="3.25"
                    stroke="white"
                    strokeWidth="2"
                  />
                  <path
                    d="M5.5 19c0-3.08 2.91-5 6.5-5s6.5 1.92 6.5 5"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
            </button>

            {/* Search Icon */}
            <button
              className="mobile-search-icon"
              onClick={() => setShowMobileSearch(!showMobileSearch)}
              aria-label="Search"
            >
              <span className="search-icon-svg"></span>
            </button>

            {/* Cart Icon */}
            <div className="mobile-cart-container">
              <button
                className="mobile-cart-icon"
                onClick={() => setShowCartDropdown(!showCartDropdown)}
                aria-label="Cart"
              >
                <span className="cart-icon-svg"></span>
                <span className="cart-count">{cartCount}</span>
              </button>

              {/* Mobile Cart Dropdown */}
              {showCartDropdown && (
                <div className="mobile-cart-dropdown">
                  <div className="mobile-cart-dropdown-header">
                    <span className="mobile-cart-dropdown-title">
                      View Cart
                    </span>
                    <div className="mobile-cart-dropdown-icon"></div>
                  </div>
                  <div className="mobile-cart-dropdown-content">
                    {cartCount > 0 ? (
                      <>
                        <div className="cart-dropdown-items">
                          {getCartItems().map((item) => (
                            <div key={item.id} className="cart-dropdown-item">
                              <div className="cart-dropdown-item-image">
                                <img
                                  src={item.image || defaultProductImage}
                                  alt={item.name}
                                  className={
                                    (item.image || defaultProductImage) ===
                                    defaultProductImage
                                      ? "cart-dropdown-item-img--default"
                                      : ""
                                  }
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = defaultProductImage;
                                    e.target.classList.add(
                                      "cart-dropdown-item-img--default",
                                    );
                                  }}
                                />
                              </div>
                              <div className="cart-dropdown-item-details">
                                <div className="cart-dropdown-item-name">
                                  {item.name}
                                </div>
                                {showPrices && (
                                  <div className="cart-dropdown-item-price">
                                    ${(item.price * item.quantity).toFixed(2)}
                                  </div>
                                )}
                              </div>
                              <button
                                className="cart-dropdown-item-delete"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  e.nativeEvent?.stopImmediatePropagation();
                                  removeFromCart(item.id);
                                  updateCartInfo();
                                }}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                                aria-label="Remove item"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                        <div className="mobile-cart-subtotal">
                          <span className="mobile-subtotal-label">
                            Cart Subtotal
                          </span>
                          {showPrices && (
                            <span className="mobile-subtotal-amount">
                              ${cartTotal.toFixed(2)}
                            </span>
                          )}
                        </div>
                        <button
                          className="mobile-quick-order-btn"
                          onClick={() => {
                            navigate("/cart");
                            setShowCartDropdown(false);
                          }}
                        >
                          View Cart
                        </button>
                        <button type="button" className="mobile-save-cart-link">
                          Save Cart for Later
                        </button>
                      </>
                    ) : (
                      <>
                        <div className="mobile-empty-cart-icon">🛒</div>
                        <div className="mobile-cart-subtotal">
                          <span className="mobile-subtotal-label">
                            Cart Subtotal
                          </span>
                          {showPrices && (
                            <span className="mobile-subtotal-amount">
                              $0.00
                            </span>
                          )}
                        </div>
                        <button type="button" className="mobile-save-cart-link">
                          Save Cart for Later
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="search-container">
            <form onSubmit={handleSearch} className="search-form">
              <div className="search-input-wrapper">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="What are you looking for?"
                  className="search-input"
                />
                <button
                  type="submit"
                  className="search-button"
                  aria-label="search"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                    <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
                  </svg>
                </button>
              </div>
            </form>
          </div>

          {/* Country Picker
          <div className="country-picker">
            <span className="country-flag"></span>
          </div> */}

          {/* Services Menu */}
          <div className="services-menu">
            <ul className="services-list">
              {/* <li className="service-item">
                <button
                  className="service-button"
                  onClick={() => setShowServicesDropdown(!showServicesDropdown)}
                >
                  <span className="service-icon">☰</span>
                  My Services
                </button>
                {showServicesDropdown && (
                  <div className="services-dropdown">
                    <ul> */}
              {/* <li>
                        <a href="/lcd-buy-back">LCD Buyback</a>
                      </li> */}
              {/* <li>
                        <a href="/blog" target="_blank" rel="noopener">
                          Blog
                        </a>
                      </li> */}
              {/* <li>
                        <a href="/support">Support</a>
                      </li>
                      <li>
                        <a href="/quick-order">Quick Order</a>
                      </li> */}
              {/* <li className="marketing-material">
                        <a href="/marketing-materials">
                          <i className="marketing-icon"></i>
                          Marketing Material
                        </a>
                      </li> */}
              {/* </ul>
                  </div>
                )}
              </li> */}
              <li className="service-item">
                <button
                  onClick={(e) => {
                    handleNavItemClick(null, e);
                    const dest = isUserLoggedIn() ? "/my-account" : "/login";
                    if (location.pathname !== dest) {
                      setIsNavigating(true);
                    }
                    navigate(dest);
                  }}
                  className="account-link"
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "0px",
                  }}
                >
                  <span className="account-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <circle
                        cx="12"
                        cy="8.5"
                        r="3.25"
                        stroke="white"
                        strokeWidth="2"
                      />
                      <path
                        d="M5.5 19c0-3.08 2.91-5 6.5-5s6.5 1.92 6.5 5"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </span>
                  <span className="account-text">My Account</span>
                </button>
              </li>
            </ul>
          </div>

          {/* Shipping Timer */}
          <div className="shipping-timer">
            <div className="timer-block">
              <div className="timer-header">
                <span
                  className={`shipping-icon ${selectedMethod?.icon || "fedex"}`}
                ></span>
                <div className="countdown">
                  {(() => {
                    const methodTime = selectedMethod
                      ? getMethodTime(selectedMethod)
                      : timeLeft;
                    return (
                      <>
                        <strong>
                          {String(methodTime.hours).padStart(2, "0")}
                        </strong>
                        <span>:</span>
                        <strong>
                          {String(methodTime.minutes).padStart(2, "0")}
                        </strong>
                        <span>:</span>
                        <strong>
                          {String(methodTime.seconds).padStart(2, "0")}
                        </strong>
                      </>
                    );
                  })()}
                </div>
              </div>
              <div className="timer-info">
                <p className="shipping-method">
                  <strong className="method-name">
                    {selectedMethod?.name || "FedEx Standard Overnight"}
                  </strong>
                </p>
                <p className="shipping-cutoff-display">
                  Cutoff: {selectedMethod?.cutoffTime || "N/A"}
                </p>
                <button
                  className="details-button"
                  onClick={() => setShowShippingDropdown(!showShippingDropdown)}
                >
                  More details
                </button>
              </div>
            </div>

            {/* Shipping Dropdown */}
            {showShippingDropdown && (
              <div className="shipping-dropdown">
                <div className="dropdown-header">
                  <div className="cutoff-text">
                    <span className="cutoff-title">
                      <strong>Cutoff</strong> Times
                    </span>
                    <span className="cutoff-subtitle">
                      Selected for display:{" "}
                      <strong>{selectedMethod?.cutoffTime || "N/A"}</strong>
                    </span>
                  </div>
                  <img
                    src={cutoffVehicleImage}
                    alt="Cutoff Vehicle"
                    className="cutoff-vehicle"
                    style={{ display: "block" }}
                  />
                </div>

                <div className="shipping-tabs">
                  <button
                    className={`tab-button ${
                      activeTab === "domestic" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("domestic")}
                  >
                    Domestic
                  </button>
                  <button
                    className={`tab-button ${
                      activeTab === "international" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("international")}
                  >
                    International
                  </button>
                </div>

                <ul className="shipping-methods">
                  {shippingMethods
                    .filter((method) => method.type === activeTab)
                    .map((method) => (
                      <li key={method.id} className="shipping-method-item">
                        <input
                          type="radio"
                          id={method.id}
                          name="shipping"
                          value={method.id}
                          checked={selectedShipping === method.id}
                          onChange={(e) => setSelectedShipping(e.target.value)}
                          className="shipping-radio"
                        />
                        <label htmlFor={method.id} className="shipping-label">
                          <span
                            className={`shipping-icon ${method.icon}`}
                          ></span>
                          <span className="method-name">{method.name}</span>
                          <span className="method-timer">
                            {(() => {
                              const methodTime = getMethodTime(method);
                              return (
                                <>
                                  <span className="timer-unit">
                                    {String(methodTime.hours).padStart(2, "0")}
                                    <small>hours</small>
                                  </span>
                                  <span className="timer-unit">
                                    {String(methodTime.minutes).padStart(
                                      2,
                                      "0",
                                    )}
                                    <small>minutes</small>
                                  </span>
                                  <span className="timer-unit">
                                    {String(methodTime.seconds).padStart(
                                      2,
                                      "0",
                                    )}
                                    <small>seconds</small>
                                  </span>
                                </>
                              );
                            })()}
                          </span>
                        </label>
                        {selectedShipping === method.id && (
                          <div className="default-timer">Default timer</div>
                        )}
                      </li>
                    ))}
                </ul>
                {/* 
                <div className="free-delivery">
                  <span>
                    <strong>Free</strong> when you spend over
                  </span>
                  <span className="divider"></span>
                  {showPrices && <span className="free-amount">$1000.00</span>}
                </div> */}
              </div>
            )}
          </div>

          {/* Cart */}
          <div className="cart-container">
            <button
              className="cart-button"
              onClick={() => setShowCartDropdown(!showCartDropdown)}
            >
              <div className="cart-icon"></div>
              <div className="cart-info">
                <strong>Cart</strong>
                {showPrices && (
                  <span className="cart-price">${cartTotal.toFixed(2)}</span>
                )}
              </div>
              <span className="cart-count">{cartCount}</span>
            </button>

            {/* Cart Dropdown */}
            {showCartDropdown && (
              <div className="cart-dropdown">
                <div className="cart-dropdown-header">
                  <span className="cart-dropdown-title">View Cart</span>
                  <div className="cart-dropdown-icon"></div>
                </div>
                <div className="cart-dropdown-content">
                  {cartCount > 0 ? (
                    <>
                      <div className="cart-dropdown-items">
                        {getCartItems().map((item) => (
                          <div key={item.id} className="cart-dropdown-item">
                            <div className="cart-dropdown-item-image">
                              <img
                                src={item.image || defaultProductImage}
                                alt={item.name}
                                className={
                                  (item.image || defaultProductImage) ===
                                  defaultProductImage
                                    ? "cart-dropdown-item-img--default"
                                    : ""
                                }
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = defaultProductImage;
                                  e.target.classList.add(
                                    "cart-dropdown-item-img--default",
                                  );
                                }}
                              />
                            </div>
                            <div className="cart-dropdown-item-details">
                              <div className="cart-dropdown-item-name">
                                {item.name}
                              </div>
                              {showPrices && (
                                <div className="cart-dropdown-item-price">
                                  ${(item.price * item.quantity).toFixed(2)}
                                </div>
                              )}
                            </div>
                            <button
                              className="cart-dropdown-item-delete"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                e.nativeEvent?.stopImmediatePropagation();
                                removeFromCart(item.id);
                                updateCartInfo();
                              }}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              aria-label="Remove item"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="cart-subtotal">
                        <span className="subtotal-label">Cart Subtotal</span>
                        {showPrices && (
                          <span className="subtotal-amount">
                            ${cartTotal.toFixed(2)}
                          </span>
                        )}
                      </div>
                      <button
                        className="quick-order-btn"
                        onClick={() => {
                          navigate("/cart");
                          setShowCartDropdown(false);
                        }}
                      >
                        View Cart
                      </button>
                      <button type="button" className="save-cart-link">
                        Save Cart for Later
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="empty-cart-icon"></div>
                      <div className="cart-subtotal">
                        <span className="subtotal-label">Cart Subtotal</span>
                        {showPrices && (
                          <span className="subtotal-amount">$0.00</span>
                        )}
                      </div>
                      <button type="button" className="save-cart-link">
                        Save Cart for Later
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        {isMobileMenuOpen && (
          <div className="mobile-menu-overlay">
            <div className="mobile-menu-container">
              <div className="mobile-menu-header">
                <h3>Menu</h3>
                <button
                  className="mobile-menu-close"
                  onClick={() => setIsMobileMenuOpen(false)}
                  aria-label="Close menu"
                >
                  ×
                </button>
              </div>
              <nav className="mobile-navigation">
                {/* Other Parts, Game Console, Accessories, Tools & Supplies, Refurbishing, Board Components — hidden from menu for now (same as desktop nav). */}
                <ul className="mobile-nav-list">
                  <li>
                    <a
                      href="/apple"
                      className={activeNavItem === "apple" ? "active" : ""}
                      onClick={(e) => {
                        handleNavItemClick("apple", e);
                        setIsMobileMenuOpen(false);
                      }}
                    >
                      Apple
                    </a>
                  </li>
                  <li>
                    <a
                      href="/samsung"
                      className={activeNavItem === "samsung" ? "active" : ""}
                      onClick={(e) => {
                        handleNavItemClick("samsung", e);
                        setIsMobileMenuOpen(false);
                      }}
                    >
                      Samsung
                    </a>
                  </li>
                  <li>
                    <a
                      href="/motorola"
                      className={activeNavItem === "motorola" ? "active" : ""}
                      onClick={(e) => {
                        handleNavItemClick("motorola", e);
                        setIsMobileMenuOpen(false);
                      }}
                    >
                      Motorola
                    </a>
                  </li>
                  <li>
                    <a
                      href="/google"
                      className={activeNavItem === "google" ? "active" : ""}
                      onClick={(e) => {
                        handleNavItemClick("google", e);
                        setIsMobileMenuOpen(false);
                      }}
                    >
                      Google
                    </a>
                  </li>
                  <li>
                    <a
                      href="/pre-owned-devices"
                      className={
                        activeNavItem === "pre-owned-devices" ? "active" : ""
                      }
                      onClick={(e) => {
                        handleNavItemClick("pre-owned-devices", e);
                        setIsMobileMenuOpen(false);
                      }}
                    >
                      Pre-Owned Devices
                    </a>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        )}

        {/* Mobile Search Dropdown */}
        {showMobileSearch && (
          <div className="mobile-search-dropdown">
            <div className="mobile-search-container">
              <form onSubmit={handleSearch} className="mobile-search-form">
                <div className="mobile-search-input-wrapper">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="What are you looking for?"
                    className="mobile-search-input"
                  />
                  <button
                    type="submit"
                    className="mobile-search-button"
                    aria-label="search"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="white"
                    >
                      <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
                    </svg>
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </header>

      {/* Search results modal: MODEL (left, 3) + PRODUCTS (right, 4 cards) */}
      {showSearchModal && (
        <>
          <div
            className="search-modal-backdrop"
            onClick={closeSearchModal}
            aria-hidden="true"
          />
          <div className="search-modal" onClick={(e) => e.stopPropagation()}>
            <div className="search-modal-inner">
              <div className="search-modal-section search-modal-models">
                <h3 className="search-modal-heading">MODEL</h3>
                {searchLoading ? (
                  <div className="search-modal-loading">Loading...</div>
                ) : (
                  <>
                    <ul className="search-modal-model-list">
                      {(searchResults.models || [])
                        .slice(0, 3)
                        .map((item, i) => {
                          const label =
                            typeof item === "object" && item?.label != null
                              ? item.label
                              : String(item ?? "");
                          const modelParam =
                            typeof item === "object" && item?.model != null
                              ? item.model
                              : label;
                          return (
                            <li key={i}>
                              <button
                                type="button"
                                className="search-modal-model-link"
                                onClick={() => {
                                  closeSearchModal();
                                  navigate(
                                    `/category/apple?model=${encodeURIComponent(modelParam)}`,
                                  );
                                }}
                              >
                                {label}
                              </button>
                            </li>
                          );
                        })}
                    </ul>
                    <button
                      type="button"
                      className="search-modal-more search-modal-more-btn"
                      onClick={() => {
                        closeSearchModal();
                        navigate(
                          `/category/apple?model=${encodeURIComponent(searchQuery || "")}`,
                        );
                      }}
                    >
                      More →
                    </button>
                    <button
                      type="button"
                      className="search-modal-view-all"
                      onClick={() => {
                        closeSearchModal();
                        navigate(
                          `/category/apple?model=${encodeURIComponent(searchQuery || "")}`,
                        );
                      }}
                    >
                      View All Products →
                    </button>
                  </>
                )}
              </div>
              <div className="search-modal-section search-modal-products">
                <h3 className="search-modal-heading">PRODUCTS</h3>
                {searchLoading ? (
                  <div className="search-modal-loading">Loading...</div>
                ) : (
                  <div className="search-modal-product-grid">
                    {(searchResults.products || [])
                      .slice(0, 4)
                      .map((product) => (
                        <button
                          type="button"
                          key={product.id}
                          className="search-modal-product-card"
                          onClick={() => {
                            closeSearchModal();
                            navigate(`/product-detail?ItemID=${product.id}`);
                          }}
                        >
                          <div className="search-modal-product-image">
                            <img
                              src={product.image || defaultProductImage}
                              alt={product.name}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = defaultProductImage;
                              }}
                            />
                          </div>
                          <div className="search-modal-product-name">
                            {product.name}
                          </div>
                          <div className="search-modal-product-sku">
                            SKU:{" "}
                            {product.sku
                              ? String(product.sku).slice(0, 4)
                              : "—"}
                          </div>
                          {showPrices && (
                            <div className="search-modal-product-price">
                              $
                              {typeof product.price === "number"
                                ? product.price.toFixed(2)
                                : product.price}
                            </div>
                          )}
                          <span
                            className={`search-modal-product-stock ${product.inStock ? "in-stock" : "out-of-stock"}`}
                          >
                            {product.inStock ? "IN STOCK" : "OUT OF STOCK"}
                          </span>
                        </button>
                      ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      <nav className="navigation">
        <div className="nav-container">
          <ul className="nav-list">
            <li>
              <a
                href="/apple"
                className={activeNavItem === "apple" ? "active" : ""}
                onClick={(e) => handleNavItemClick("apple", e)}
              >
                Apple
              </a>
            </li>
            <li>
              <a
                href="/samsung"
                className={activeNavItem === "samsung" ? "active" : ""}
                onClick={(e) => handleNavItemClick("samsung", e)}
              >
                Samsung
              </a>
            </li>
            <li>
              <a
                href="/motorola"
                className={activeNavItem === "motorola" ? "active" : ""}
                onClick={(e) => handleNavItemClick("motorola", e)}
              >
                Motorola
              </a>
            </li>
            <li>
              <a
                href="/google"
                className={activeNavItem === "google" ? "active" : ""}
                onClick={(e) => handleNavItemClick("google", e)}
              >
                Google
              </a>
            </li>
            <li>
              <a
                href="/pre-owned-devices"
                className={
                  activeNavItem === "pre-owned-devices" ? "active" : ""
                }
                onClick={(e) => handleNavItemClick("pre-owned-devices", e)}
              >
                Pre-Owned Devices
              </a>
            </li>
          </ul>
        </div>
      </nav>

      {/* Navigation Dropdown - Simple UI */}
      {activeNavItem && (
        <div
          className={`navigation-dropdown ${isClosing ? "closing" : "show"}`}
          onClick={(e) => {
            // Close whenever click happens outside dropdown content panel
            if (!e.target.closest(".dropdown-container")) {
              handleNavItemClick(null, e);
            }
          }}
        >
          {/* Mobile Close Button */}
          <button
            className="mobile-dropdown-close"
            onClick={(e) => handleNavItemClick(null, e)}
            aria-label="Close dropdown"
          >
            ×
          </button>
          <div className="dropdown-container">
            {/* Search Input Field */}
            <div className="dropdown-search-container">
              <div className="search-input-wrapper">
                <div className="search-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                    <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="What are you looking for?"
                  className="dropdown-search-input"
                  value={navDropdownQuery}
                  onChange={(e) => setNavDropdownQuery(e.target.value)}
                  aria-label="Search models for this category"
                  autoComplete="off"
                />
                <div
                  className={`clear-icon ${!navDropdownQuery.trim() ? "clear-icon--hidden" : ""}`}
                  onClick={() => setNavDropdownQuery("")}
                  role="button"
                  tabIndex={navDropdownQuery.trim() ? 0 : -1}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setNavDropdownQuery("");
                    }
                  }}
                  aria-label="Clear search"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                  </svg>
                </div>
              </div>
            </div>

            {scopeNavForApi &&
              navDropdownQuery.trim().length >= 2 &&
              (navDropdownSearchLoading || navDropdownApiExtras.length > 0) && (
                <div
                  className="nav-dropdown-search-api"
                  role="region"
                  aria-label="Additional model matches"
                >
                  {navDropdownSearchLoading && (
                    <div className="nav-dropdown-search-api-status">
                      Searching…
                    </div>
                  )}
                  {!navDropdownSearchLoading &&
                    navDropdownApiExtras.length > 0 && (
                      <ul className="nav-dropdown-search-api-list">
                        {navDropdownApiExtras.map((m, idx) => {
                          const label = [m.Make, m.Brand, m.Model]
                            .filter(Boolean)
                            .join(" ");
                          const modelName = m.Model || "";
                          if (!modelName) return null;
                          return (
                            <li key={`${modelName}-${idx}`}>
                              <button
                                type="button"
                                className="nav-dropdown-search-api-link"
                                onClick={(e) =>
                                  handleOptionSelect(modelName, e)
                                }
                              >
                                {label || modelName}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                </div>
              )}

            <div className="dropdown-content">
              {/* Left Column */}
              <div className="main-categories-column">
                {/* Main Categories Banner */}

                <ul className="main-categories-list">
                  {activeNavItem === "accessories" ? null : (
                    <div className="main-categories-banner">
                      <div className="banner-icon">
                        <svg
                          width="9"
                          height="10"
                          viewBox="0 0 9 10"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M0.999959 5.66667L4.33329 9L7.66663 5.66667"
                            stroke="#FF0000"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M0.999959 1.00001L4.33329 4.33334L7.66663 1.00001"
                            stroke="#FF0000"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                      </div>
                      <span className="dropdown-banner-text">
                        Main Categories
                      </span>
                      <div className="banner-close">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="#d32f2f"
                        >
                          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                        </svg>
                      </div>
                    </div>
                  )}

                  {(activeNavItem === "apple" ||
                    activeNavItem === "samsung" ||
                    activeNavItem === "motorola" ||
                    activeNavItem === "google") &&
                    brandsForSearch &&
                    brandsForSearch.length > 0 && (
                      <>
                        {brandsForSearch.map((brand, brandIndex) => {
                          const isSelected =
                            selectedBrandName === brand.BrandName;
                          return (
                            brand.BrandName && (
                              <li
                                key={brandIndex}
                                className={`main-category-item ${
                                  isSelected ? "selected-brand" : ""
                                }`}
                                onMouseEnter={() =>
                                  setHoveredBrandName(brand.BrandName)
                                }
                                onMouseLeave={() => setHoveredBrandName(null)}
                              >
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    if (isSelected) {
                                      setSelectedBrandName(null);
                                    } else {
                                      setSelectedBrandName(brand.BrandName);
                                    }
                                  }}
                                  className={
                                    isSelected ? "selected-brand-link" : ""
                                  }
                                >
                                  {isSelected && (
                                    <span className="brand-pin-icon">
                                      <svg
                                        width="12"
                                        height="12"
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                      >
                                        <path d="M17 4v7l2 3v2h-6v5l-1 1-1-1v-5H5v-2l2-3V4c0-1.1.9-2 2-2h6c1.1 0 2 .9 2 2z" />
                                      </svg>
                                    </span>
                                  )}
                                  {brand.BrandName}
                                  {isSelected && (
                                    <span
                                      className="brand-close-icon"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setSelectedBrandName(null);
                                      }}
                                    >
                                      <svg
                                        width="12"
                                        height="12"
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                      >
                                        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                                      </svg>
                                    </span>
                                  )}
                                </button>
                              </li>
                            )
                          );
                        })}
                      </>
                    )}
                  {activeNavItem === "accessories" && (
                    <>
                      <li className="main-category-item accessories-category">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            handleCategoryClick("accessories");
                          }}
                        >
                          <div className="category-image-placeholder">
                            {/* Image will be added here later */}
                          </div>
                          <span>Recently Added</span>
                        </button>
                      </li>
                      <li className="main-category-item accessories-category">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            handleCategoryClick("accessories");
                          }}
                        >
                          <div className="category-image-placeholder">
                            {/* Image will be added here later */}
                          </div>
                          <span>Casper</span>
                        </button>
                      </li>
                      <li className="main-category-item accessories-category">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            handleCategoryClick("accessories");
                          }}
                        >
                          <div className="category-image-placeholder">
                            {/* Image will be added here later */}
                          </div>
                          <span>OEM Accessories</span>
                        </button>
                      </li>
                      <li className="main-category-item accessories-category">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            handleCategoryClick("accessories");
                          }}
                        >
                          <div className="category-image-placeholder">
                            {/* Image will be added here later */}
                          </div>
                          <span>Ampsentrix</span>
                        </button>
                      </li>
                      <li className="main-category-item accessories-category">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            handleCategoryClick("accessories");
                          }}
                        >
                          <div className="category-image-placeholder">
                            {/* Image will be added here later */}
                          </div>
                          <span>Console Accessories</span>
                        </button>
                      </li>
                      <li className="main-category-item accessories-category">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            handleCategoryClick("accessories");
                          }}
                        >
                          <div className="category-image-placeholder">
                            {/* Image will be added here later */}
                          </div>
                          <span>Memory</span>
                        </button>
                      </li>
                      <li className="main-category-item accessories-category">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            handleCategoryClick("accessories");
                          }}
                        >
                          <div className="category-image-placeholder">
                            {/* Image will be added here later */}
                          </div>
                          <span>Skins</span>
                        </button>
                      </li>
                    </>
                  )}
                </ul>
              </div>

              {/* Right Columns - Multiple columns side by side, each showing a brand */}
              <div className="category-columns-container">
                {(activeNavItem === "apple" ||
                  activeNavItem === "samsung" ||
                  activeNavItem === "motorola" ||
                  activeNavItem === "google") &&
                  brandsForSearch &&
                  brandsForSearch.length > 0 && (
                    <>
                      {brandsForSearch
                        .filter((brand) => {
                          // Show selected brand, or hovered brand if nothing selected, or all if neither
                          if (selectedBrandName) {
                            return brand.BrandName === selectedBrandName;
                          }
                          if (hoveredBrandName) {
                            return brand.BrandName === hoveredBrandName;
                          }
                          return true;
                        })
                        .map(
                          (brand, brandIndex) =>
                            brand.BrandName &&
                            brand.Model &&
                            brand.Model.length > 0 && (
                              <div key={brandIndex} className="category-column">
                                <div className="category-header">
                                  <strong className="category-name brand-name-header">
                                    {brand.BrandName}
                                  </strong>
                                </div>
                                <ul className="model-list">
                                  {brand.Model.map((model, modelIndex) => (
                                    <li
                                      key={modelIndex}
                                      className={`model-item ${
                                        selectedOptions[activeNavItem] ===
                                        model.ModelName
                                          ? "selected-option"
                                          : ""
                                      }`}
                                    >
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          handleOptionSelect(
                                            model.ModelName,
                                            e,
                                          );
                                        }}
                                      >
                                        {model.ModelName}
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ),
                        )}
                    </>
                  )}
                {activeNavItem === "accessories" && (
                  <>
                    <div className="category-column accessories-products">
                      <div className="category-header">
                        <div className="category-name">
                          <span className="category-name-recently">
                            RECENTLY
                          </span>
                          <span className="category-name-added">ADDED</span>
                        </div>
                        <span className="category-subtitle">New Added!</span>
                      </div>
                      <div className="products-grid">
                        {/* Row 1 */}
                        <div className="product-row">
                          <div className="product-card">
                            <div className="product-image-placeholder">
                              {/* Image will be added here later */}
                            </div>
                            <div className="product-name">
                              USB-C Magnetic Watch Charging Cable (3.3ft.) For
                              All Watch Series...
                            </div>
                          </div>
                          <div className="product-card">
                            <div className="product-image-placeholder">
                              {/* Image will be added here later */}
                            </div>
                            <div className="product-name">
                              240W USB-C To USB-C Cable (6ft.) Compatible For
                              MacBook / IMac...
                            </div>
                          </div>
                          <div className="product-card">
                            <div className="product-image-placeholder">
                              {/* Image will be added here later */}
                            </div>
                            <div className="product-name">
                              USB-C To C (3ft) Cable For Samsung (OEM Grade New)
                              (Bulk Packaging)...
                            </div>
                          </div>
                          <div className="product-card">
                            <div className="product-image-placeholder">
                              {/* Image will be added here later */}
                            </div>
                            <div className="product-name">
                              USB-C To C (3ft) Cable For Samsung (OEM Grade New)
                              (Bulk Packaging)...
                            </div>
                          </div>
                        </div>
                        {/* Row 2 */}
                        <div className="product-row">
                          <div className="product-card">
                            <div className="product-image-placeholder">
                              {/* Image will be added here later */}
                            </div>
                            <div className="product-name">
                              Casper Tempered Glass Compatible For Nintendo
                              Switch OLED (Retail...
                            </div>
                          </div>
                          <div className="product-card">
                            <div className="product-image-placeholder">
                              {/* Image will be added here later */}
                            </div>
                            <div className="product-name">
                              Casper Pro Tempered Glass Compatible For IPhone 13
                              / 13 Pro/...
                            </div>
                          </div>
                          <div className="product-card">
                            <div className="product-image-placeholder">
                              {/* Image will be added here later */}
                            </div>
                            <div className="product-name">
                              Casper Pro Tempered Glass Compatible For IPhone 13
                              / 13 Pro/...
                            </div>
                          </div>
                          <div className="product-card view-all-card">
                            <div className="view-all-content">
                              <span className="view-all-text">View all</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NavbarComponent;
